import type { ReactElement } from 'react';
import { Children, cloneElement } from 'react';
import { vars } from '@etg/wings';
import { css, keyframes } from '@eti/styles';
import { useDirection } from '@eti/providers';

const shine = (isRTL: boolean) => keyframes`
  0% {
    ${isRTL ? 'right: -400px;' : 'left: -400px;'}
  }
  100% {
    ${isRTL ? 'right: 600px;' : 'left: 600px;'}
  }
`;

const boxStyles = (backgroundColor: string, isRTL: boolean, height?: number, width?: number) => css`
  ${`background-color: ${backgroundColor || '#e5e5e5;'}`};

  height: ${`${height}px`};
  overflow: hidden;
  position: relative;
  width: ${width ? `${width}%` : '100%'};

  &::before {
    animation: ${`${shine(isRTL)} 1.5s infinite;`};
    background-image: linear-gradient(to right, #e5e5e500 0%, #fffc 50%, #e5e5e500 100%);
    background-repeat: repeat-x;
    content: '';
    height: 100%;
    position: absolute;
    transform: skewX(-45deg);
    width: 200px;
  }
`;

interface BoxProps {
  backgroundColor?: string;
  children?: ReactElement;
  height: number;
  marginBottom?: number;
  width?: number;
}
const Box = ({
  children,
  marginBottom = 20,
  backgroundColor = `${vars.colors.page.background.primary}`,
  height,
  width,
}: BoxProps) => {
  const { isRTL } = useDirection();

  if (!children) {
    return (
      <div
        className={boxStyles(backgroundColor, isRTL, height, width)}
        data-testid="skeleton-box-without-children"
      />
    );
  }

  return (
    <>
      <div className={boxStyles(backgroundColor, isRTL, height, width)} />
      {Children.map(children, (child) => cloneElement(child, { margin: marginBottom / 2 }))}
    </>
  );
};

export default {
  Box,
};
